import { useState } from "react";
import type { FormEvent } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db1n7o0m252sfkho1u20";

const contactDetails = [
  { icon: "ri-phone-line", label: "Toll-free", value: "1800 570 0229 · 1800 570 0232", href: "tel:18005700229" },
  { icon: "ri-mail-line", label: "Email", value: "sales@djtevretrofitting.in", href: "mailto:sales@djtevretrofitting.in" },
  {
    icon: "ri-map-pin-2-line",
    label: "Head office",
    value: "Siri Sampada Arcade, Gachibowli, Hyderabad, Telangana 500104",
    href: "#contact",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    setStatus("loading");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFormError("");
        setMessage("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg && !isSpam
            ? serverMsg
            : "Something went wrong sending your request. Please check your details and try again.",
        );
      }
    } catch {
      setStatus("error");
      setFormError("We couldn't reach the server. Please check your connection and try again.");
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-secondary-950 py-20 md:py-28">
      <div className="absolute -left-20 top-1/4 h-72 w-72 rounded-full bg-primary-500/15 blur-3xl" />
      <div className="relative mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="reveal">
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.16em] text-primary-200">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
              Get in touch
            </span>
            <h2 className="mt-4 font-heading text-3xl font-semibold leading-tight text-background-50 md:text-4xl">
              Talk to us about green iron
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-background-300">
              Tell us about your steel plant, volumes, and timeline. We will send a tailored offtake proposal with
              a verified carbon estimate for your supply chain.
            </p>

            <div className="mt-9 space-y-4">
              {contactDetails.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-4 rounded-lg border border-background-50/10 bg-background-50/[0.04] p-4 transition-colors hover:border-primary-400/50"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-primary-500/20 text-primary-200">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-wider text-background-400">{item.label}</span>
                    <span className="block font-medium text-background-50">{item.value}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div className="reveal rounded-xl border border-background-50/10 bg-background-50 p-6 md:p-8">
            {status === "success" ? (
              <div className="flex h-full min-h-[24rem] flex-col items-center justify-center text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-check-line text-3xl" />
                </span>
                <h3 className="mt-5 font-heading text-xl font-semibold text-foreground-950">Request received</h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
                  Thanks! Your request has reached our team. A specialist will reply within one business day with
                  a tailored offtake proposal and carbon estimate.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form data-readdy-form id="greensteel-quote-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="gs-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      Full name
                    </label>
                    <input
                      id="gs-name"
                      name="name"
                      type="text"
                      required
                      placeholder="Jane Doe"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="gs-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      Work email
                    </label>
                    <input
                      id="gs-email"
                      name="email"
                      type="email"
                      required
                      placeholder="jane@company.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="gs-company" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      Company
                    </label>
                    <input
                      id="gs-company"
                      name="company"
                      type="text"
                      required
                      placeholder="Acme Industries"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="gs-product" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      Product interest
                    </label>
                    <select
                      id="gs-product"
                      name="product_interest"
                      defaultValue="Green HBI"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    >
                      <option>Green HBI</option>
                      <option>TMT reinforcement bars</option>
                      <option>Wire rods</option>
                      <option>Heavy plates</option>
                      <option>Hot-rolled coil</option>
                      <option>Structural H-beam</option>
                      <option>Oxygen by-product</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="gs-volume" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    Estimated annual volume
                  </label>
                  <select
                    id="gs-volume"
                    name="volume"
                    defaultValue="100 - 1,000 tons"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  >
                    <option>25 - 100 tons (trial)</option>
                    <option>100 - 1,000 tons</option>
                    <option>1,000 - 10,000 tons</option>
                    <option>10,000+ tons (offtake)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="gs-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    Project details
                  </label>
                  <textarea
                    id="gs-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    required
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="Tell us about grades, timelines, and delivery locations..."
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  />
                  <span className="mt-1 block text-right text-xs text-foreground-400">{message.length}/500</span>
                </div>

                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />

                {status === "error" && formError && (
                  <p className="rounded-md bg-accent-100 px-3.5 py-2.5 text-sm text-accent-900">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Request my quote
                      <i className="ri-send-plane-line" />
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-foreground-400">
                  We reply within one business day. Your details stay private.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}